import React, { useMemo, useEffect, useState, useCallback } from 'react';
import PapaParse from 'papaparse';

import { INamedObjectTypes } from '@scale/shared/types/lookup';
import { QualificationType } from '@scale/shared/types/qualification/types';
import { ScreeningPendingReview } from '@scale/shared/taskerScreening/types';
import { defaultBenchmark } from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningBenchmarkCreationTool';
import API from '@scale/frontend-lib/dashboard-api';

import { Accordion, Button, Spinner } from '@scale/scaleui';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ToggleButtonGroup,
} from 'react-scale/components/design-system';
import SelectCourse from 'react-scale/corp/components/SelectCourse';
import SelectQualification from 'react-scale/corp/components/SelectQualification';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import FileUpload from 'react-scale/dashboard.scale.com/components/SelfServe/Upload/FileUpload';
import Table from 'corp/components/UI/Table';

import TaskerScreeningGradingReviewModal from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningGradingReviewModal';
import { WorkerSkillDimension } from '@scale/shared-types';
import { QualificationSelectOption } from '@scale/shared/types/params/course';

const tableHeaders: Record<string, string> = {
  preview: 'Preview',
  type: 'Type',
  workerSkillName: 'Skill',
  workerSkillId: 'Skill ID',
  course: 'Course',
  sectionTitle: 'Course Section Title',
  expectedScore: 'Expected Score',
  data: 'Data',
};

const shownTableHeaders = [
  'preview',
  'type',
  'workerSkillName',
  'course',
  'sectionTitle',
  'expectedScore',
];

const smallColumns = ['preview', 'type', 'expectedScore'];

interface TaskerScreeningBenchmarksCsvUploadModalProps {
  modalOpen: boolean;
  setModalOpen: (modalOpen: boolean) => void;
  setNeedsFetch: (needsFetch: boolean) => void;
}

type ExtendedScreeningPendingReview = ScreeningPendingReview & { expectedScore: number };

const TaskerScreeningBenchmarksCsvUploadModal: React.FC<
  TaskerScreeningBenchmarksCsvUploadModalProps
> = ({ modalOpen, setModalOpen, setNeedsFetch }) => {
  // States
  // Contains data for all the rows in the CSV
  const [data, setData] = useState<ExtendedScreeningPendingReview[]>([]);
  const [isResponse, setIsResponse] = useState<boolean>(true); // Otherwise is re-write
  const [currentParams, setCurrentParams] = useState<Record<string, string>>({
    course: defaultBenchmark.course,

    workerSkillId: defaultBenchmark.workerSkillId ?? '',
    workerSkillName: defaultBenchmark.workerSkillName ?? '',
  }); // Needed for handling nully values from Select components

  // States for select components
  const [selectedCourse, setSelectedCourse] = useState<{ label: string; value: string }>({
    label: defaultBenchmark.course,
    value: defaultBenchmark.course,
  });
  const [selectedQualification, setSelectedQualification] = useState<QualificationSelectOption>({
    qualificationType: QualificationType.WorkerSkill,
    workerSkill: {
      dimension: defaultBenchmark.workerSkillDimension ?? '',
      label: defaultBenchmark.workerSkillName ?? '',
      value: defaultBenchmark.workerSkillId ?? '',
    },
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showPreviewRow, setShowPreviewRow] = useState<boolean>(false);
  const [previewRow, setPreviewRow] = useState<ScreeningPendingReview | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sectionTitles, setSectionTitles] = useState<string[]>([]);

  useEffect(() => {
    const fetchSectionTitles = async () => {
      const { sectionTitles } = await API.genai.fetchUniqueSectionTitles();
      setSectionTitles(sectionTitles);
      setIsLoading(false);
    };

    setIsLoading(true);
    fetchSectionTitles();
  }, []);

  const resetFields = () => {
    setData([]);
    setIsLoading(false);
    setIsResponse(true);
    setShowPreviewRow(false);
    setPreviewRow(null);
    setErrorMsg(null);
    setSelectedCourse({ label: defaultBenchmark.course, value: defaultBenchmark.course });
    setSelectedQualification({
      qualificationType: QualificationType.WorkerSkill,
      workerSkill: {
        label: defaultBenchmark.workerSkillName ?? '',
        value: defaultBenchmark.workerSkillId ?? '',
        dimension: defaultBenchmark.workerSkillDimension ?? '',
      },
    });
    setCurrentParams({
      course: defaultBenchmark.course,
      workerSkillName: defaultBenchmark.workerSkillName ?? '',
      workerSkillId: defaultBenchmark.workerSkillId ?? '',
      workerSkillDimension: defaultBenchmark.workerSkillDimension ?? '',
    });
  };

  const handleUpdateWorkerSkill = useCallback(
    (value: any) => {
      const updatedQualOption = selectedQualification;
      if (Object.values(WorkerSkillDimension).includes(value)) {
        if (!updatedQualOption.workerSkill) {
          updatedQualOption.workerSkill = {
            label: '',
            value: '',
            dimension: value,
          };
        } else {
          updatedQualOption.workerSkill.dimension = value;
        }
      } else {
        updatedQualOption.workerSkill = {
          ...updatedQualOption.workerSkill,
          ...value,
        };
      }
      setSelectedQualification({ ...updatedQualOption });
      if (updatedQualOption) {
        setCurrentParams({
          ...currentParams,
          workerSkillName: updatedQualOption.workerSkill?.label ?? '',
          workerSkillId: updatedQualOption.workerSkill?.value ?? '',
          workerSkillDimension: updatedQualOption.workerSkill?.dimension ?? '',
        });
      }
    },
    [selectedQualification, setSelectedQualification, currentParams, setCurrentParams],
  );

  const handleSubmit = async () => {
    await API.genai.publishBulkScreeningBenchmark({
      data: data.map(row => ({
        enabled: true,
        workerSkillId: row.workerSkillId ?? '',
        workerSkillDimension: row.workerSkillDimension ?? '',
        sectionTitle: row.sectionTitle,
        questions: row.questions,
        answers: row.answers,
        isRewrite: row.isRewrite,
        rewrittenOriginalAnswers: row.rewrittenOriginalAnswers,
        description: row.description,
        course: row.course,
        expectedScore: row.expectedScore,
      })),
    });
    setModalOpen(false);
    setNeedsFetch(true);
  };

  const handleCancel = () => {
    resetFields();
    setModalOpen(false);
  };

  const handleUploadCsv = (files: File[]) => {
    setIsLoading(true);
    PapaParse.parse(files[0], {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: async (results: any) => {
        if (results.data && results.data.length > 0) {
          setIsLoading(true);
          setErrorMsg(null);
          // unload based on the data keys dynamically
          const tempArr = [];

          for (const row of results.data) {
            const { sectionTitle, expectedScore, description } = row;
            if (sectionTitles.indexOf(sectionTitle) === -1) {
              setErrorMsg(
                `Section title "${sectionTitle}" is not valid - it does not currently exist.`,
              );
              setIsLoading(false);
              return;
            }

            const questions: string[] = [];
            const answers: string[] = [];
            const rewrittenOriginalAnswers: string[] = [];
            // TODO: (jonathan.wang) This logic is not the best right now, length and iterates
            const maxQuestions = Object.keys(row)
              .filter(key => key.includes('question') && row[key] && row[key] !== '')
              .map(key => Number(key.split('question')[1])).length;

            if (maxQuestions) {
              for (let i = 1; i <= maxQuestions; i++) {
                questions.push(row[`question${i}`]);
                answers.push(row[`answer${i}`]);
                if (!isResponse) rewrittenOriginalAnswers.push(row[`rewrittenOriginalAnswer${i}`]);
              }
            }

            const tempObj = {
              ...defaultBenchmark,
              workerSkillId: currentParams.workerSkillId,
              workerSkillName: currentParams.workerSkillName, // TEMP: Do we need the name?
              workerSKillDimension: currentParams.workerSkillDimension,
              course: currentParams.course,
              sectionTitle,
              expectedScore,
              isRewrite: !isResponse,
              description,
              questions,
              answers,
              rewrittenOriginalAnswers,
            };

            tempArr.push(tempObj);
          }
          setData(tempArr);
          setIsLoading(false);
        }
      },
    });
  };

  const tableData = useMemo(() => {
    const tempData = data.map(row => ({
      preview: (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            const { expectedScore, ...rest } = row; // Remove expectedScore for typing
            setPreviewRow(rest);
            setShowPreviewRow(true);
          }}
        >
          Preview
        </Button>
      ),
      type: row.isRewrite ? 'Text Rewrite' : 'Text',
      workerSkillName: row.workerSkillName,
      workerSkillId: row.workerSkillId,
      course: row.course,
      sectionTitle: row.sectionTitle,
      expectedScore: row.expectedScore,
    }));
    return tempData;
  }, [data]);

  const columns = useMemo(() => {
    return Object.keys(tableHeaders).map(label => ({
      Header: tableHeaders[label],
      accessor: label,
      show: shownTableHeaders.includes(label),
      filterable: false,
      ...(smallColumns.includes(label) && { minWidth: 60 }),
    }));
  }, []);

  const renderInstructions = (
    <div className="col-span-6">
      <div className="p-1">
        Ensure all of (sectionTitle, description, expectedScore) columns are filled out. Additional
        questions can be added, but make sure to follow the column naming format.
      </div>
      <a
        className="p-1"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#DD4FDF' }}
        href="https://docs.google.com/spreadsheets/d/1L3etCjpw94VCdo7p9i-vHV_0i5ZfadAoRckXorVJeDg/edit?usp=sharing"
      >
        Example of a spreadsheet. Download, delete example rows and make edits, then upload.
      </a>
    </div>
  );

  const renderOptions = (
    <div className={'col-span-6 flex justify-between items-start pb-4'}>
      <SmartLink type={INamedObjectTypes.Course} id={currentParams.course} />
      <div className="w-[300px]">
        <SelectCourse
          value={selectedCourse}
          onChange={changeCourse => {
            setSelectedCourse(changeCourse);
            if (changeCourse) {
              setCurrentParams({ ...currentParams, course: changeCourse.value });
            }
          }}
        />
      </div>
      <div className="w-[300px] pl-4">
        <SelectQualification
          value={selectedQualification}
          onWorkerSkillChange={handleUpdateWorkerSkill}
          qualificationType={QualificationType.WorkerSkill}
          requireStatus={false}
        />
      </div>
      <ToggleButtonGroup
        disabled={data.length > 0}
        className={'p-2 mt-1'}
        value={isResponse}
        onChange={(value: boolean | null) => {
          if (value !== null) setIsResponse(value);
        }}
        options={[
          { value: true, label: 'Text Response' },
          { value: false, label: 'Text Response Rewrite' },
        ]}
      />
    </div>
  );

  const renderUpload = (
    <div className="col-span-6">
      {isLoading ? (
        <Spinner size="lg" />
      ) : (
        <FileUpload allowedMimeTypes=".csv" onFiles={handleUploadCsv} />
      )}
    </div>
  );

  const renderTable = (
    <div className="col-span-6">
      <Accordion header="Uploaded CSV" target={<Table data={tableData} columns={columns} />} />
    </div>
  );

  const renderPreview = <div className="col-span-6"></div>;

  return (
    <>
      {previewRow && (
        <TaskerScreeningGradingReviewModal
          open={showPreviewRow}
          setOpen={setShowPreviewRow}
          review={previewRow}
          updateReviewScore={() => {}}
        />
      )}
      <Dialog fullWidth maxWidth="lg" open={modalOpen} onClose={handleCancel}>
        <DialogTitle>Upload Benchmarks in Bulk - CSV</DialogTitle>
        <DialogContent className="scale-tailwind scale-theme scaleui overflow-y-auto max-h-[70vh] h-fit">
          <div className="grid grid-cols-6 gap-4 pb-2 w-full auto-rows-auto">
            {renderInstructions}
            {renderOptions}
            {renderTable}
            {data.length === 0 ? renderUpload : renderPreview}
          </div>
        </DialogContent>
        <DialogActions className="scale-tailwind scale-theme scaleui">
          {errorMsg && (
            <div className={'scaleui text-danger-500 flex items-center'}>ERROR: {errorMsg}</div>
          )}
          <Button variant="primary" size="lg" onClick={handleSubmit} disabled={data.length === 0}>
            Submit
          </Button>
          <Button variant="neutral" size="lg" onClick={resetFields}>
            Reset
          </Button>
          <Button variant="neutral" size="lg" onClick={handleCancel}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TaskerScreeningBenchmarksCsvUploadModal;
